import { ChangeDetectionStrategy, Component, HostListener, Input, forwardRef, ChangeDetectorRef, Inject } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { INotificationsService, INOTIFICATIONS_SERVICE } from '@platform500services/p500-ui-kit';

@Component({
  selector: 'gtd-form-field-image-uploader',
  templateUrl: './image-uploader.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUploaderComponent),
      multi: true,
    },
  ],
})
export class ImageUploaderComponent implements ControlValueAccessor {
  @Input() hint: string;
  @Input() fileSizeLimit = 5;
  @Input() widthControl = new UntypedFormControl();
  @Input() heightControl = new UntypedFormControl();
  @Input() accept = 'image/png, image/jpeg, image/svg+xml';
  @Input() additional = 'JPG, PNG, SVG';
  @Input() required = false;
  @Input() label: string;
  @Input() handleFile: (file: File) => void;

  public value: string | SafeResourceUrl;
  public imageData: any = {};
  private onChange: any = () => {};
  private onTouch: any = () => {};

  constructor(
    @Inject(INOTIFICATIONS_SERVICE) private notificationsService: INotificationsService,
    private cd: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  @HostListener('change', ['$event.target.files']) emitFiles(event: FileList) {
    const file = event && event.item(0);
    if (!this.accept.includes(file.type)) {
      this.notificationsService.add(
        {
          type: 'error',
          title: $localize`:@@image_uploader.invalid_file_type:Invalid file type`,
        },
        true
      );
      return;
    }
    if (file.size > 1024 * 1024 * this.fileSizeLimit) {
      this.notificationsService.add(
        {
          type: 'error',
          title: $localize`:@@image_uploader.max_file_size_is:Max file size is` + ` ${this.fileSizeLimit} MB`,
        },
        true
      );
      return;
    }
    this.onTouch();
    this.getBase64(file).then((data) => {
      const needle = 'base64,';
      this.value = this.sanitizer.bypassSecurityTrustResourceUrl(data);
      const base64Value = data.slice(data.search(needle) + needle.length);
      this.imageData.format = file.type.split('/')[1].toUpperCase();
      this.imageData.size = this.fileSize(file.size);
      this.setRemoteImageResolution(data);
      this.onChange(base64Value);
      if (this.handleFile) {
        this.handleFile(file);
      }
      this.cd.detectChanges();
    });
  }

  getBase64(file): any {
    return new Promise<any>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  writeValue(value: any): void {
    this.value = value;
    if (!value) {
      return;
    }
    this.imageData.format = this.getFormatFromName(value);
    this.setRemoteImageResolution(value);
  }

  fileSize(sizeInBit: number): string {
    const sizeInKb = Math.round((sizeInBit * 100) / 1024) / 100;
    if (sizeInKb < 1024) {
      return `${sizeInKb} kb`;
    } else {
      const sizeInMb = Math.round((sizeInKb * 100) / 1024) / 100;
      return `${sizeInMb} mb`;
    }
  }

  getFormatFromName(name: string): string {
    const nameArr = name.split('.');
    if (nameArr && nameArr.length > 1) {
      return nameArr[nameArr.length - 1];
    } else {
      return null;
    }
  }

  setRemoteImageResolution(url: string): void {
    const img = new Image();
    img.src = url;
    img.onload = () => {
      this.imageData.resolution = `${img.width}x${img.height}`;
      this.widthControl.setValue(img.width);
      this.heightControl.setValue(img.height);
      this.cd.detectChanges();
    };
  }

  removeImage() {
    this.value = null;
    this.imageData = {};
    this.cd.detectChanges();
    this.onTouch();
    this.onChange(null);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
}
